import { Injectable } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UploadService } from './upload.service';
import { InteractionsService } from './interactions.service';
import { CustomerDetailsManagementService } from './customer-details-management.service';
import { PhoneFormatPipe } from '../shared/pipes';
import { MatMenuTrigger } from '@angular/material/menu';
import { CompanyEmail, CompanyPhone, RelatedCustomer, InsuranceCompany } from '../interfaces/insurance-company.interface';

export interface ContactValidationRequest {
  contact_value: string;
  valid_status: boolean;
  contact_type: 'email' | 'phone';
}

export interface ContactEditRequest {
  old_value: string;
  new_value: string;
  contact_type: 'email' | 'phone';
}

export interface ContactContext {
  contactValue: string;
  contactType: 'email' | 'phone';
  context: 'company' | 'customer';
  customerId?: number;
  companyId?: number;
}

export interface ValidationResult {
  success: boolean;
  unchanged?: boolean;
  message: string;
}

/**
 * Service for managing contact (email/phone) operations in customer details
 */
@Injectable({
  providedIn: 'root'
})
export class ContactManagementService {
  private phoneFormatPipe = new PhoneFormatPipe();

  constructor(
    private uploadService: UploadService,
    private interactionsService: InteractionsService,
    private managementService: CustomerDetailsManagementService
  ) {}

  /**
   * Add email to company
   */
  addCompanyEmail(companyId: number, email: string): Observable<any> {
    if (!email?.trim()) {
      return throwError(() => new Error('Email cím megadása kötelező'));
    }

    return this.managementService.addCompanyEmail(companyId, email);
  }

  /**
   * Add phone to company
   */
  addCompanyPhone(companyId: number, phone: string): Observable<any> {
    if (!phone?.trim()) {
      return throwError(() => new Error('Telefonszám megadása kötelező'));
    }

    const formattedPhone = this.phoneFormatPipe.transform(phone.trim());
    return this.managementService.addCompanyPhone(companyId, formattedPhone);
  }

  /**
   * Add email to customer
   */
  addCustomerEmail(customerId: number, email: string): Observable<any> {
    if (!email?.trim()) {
      return throwError(() => new Error('Email cím megadása kötelező'));
    }

    const emailData = { email: email.trim() };
    return this.uploadService.addCustomerEmail(customerId, emailData);
  }

  /**
   * Add phone to customer
   */
  addCustomerPhone(customerId: number, phone: string): Observable<any> {
    if (!phone?.trim()) {
      return throwError(() => new Error('Telefonszám megadása kötelező'));
    }

    const formattedPhone = this.phoneFormatPipe.transform(phone.trim());
    const phoneData = { phone: formattedPhone };
    return this.uploadService.addCustomerPhone(customerId, phoneData);
  }

  /**
   * Validate contact (mark as complete/valid)
   */
  validateContact(context: ContactContext, validStatus: boolean): Observable<ValidationResult> {
    const updateRequest: ContactValidationRequest = {
      contact_value: context.contactValue,
      valid_status: validStatus,
      contact_type: context.contactType
    };

    // Determine endpoint based on context
    if (context.context === 'customer' && context.customerId) {
      return this.uploadService.updateCustomerContactValidStatus(context.customerId, updateRequest)
        .pipe(
          map((response: any) => ({
            success: true,
            unchanged: response.unchanged,
            message: this.getValidationMessage(context.contactValue, validStatus, response.unchanged)
          })),
          catchError(error => throwError(() => ({ 
            success: false, 
            message: 'Hiba történt a státusz frissítésekor' 
          })))
        );
    } else if (context.companyId) {
      return this.uploadService.updateContactValidStatus(context.companyId, updateRequest)
        .pipe(
          map((response: any) => ({
            success: true,
            unchanged: response.unchanged,
            message: this.getValidationMessage(context.contactValue, validStatus, response.unchanged)
          })),
          catchError(error => throwError(() => ({ 
            success: false, 
            message: 'Hiba történt a státusz frissítésekor' 
          })))
        );
    }

    return throwError(() => ({ 
      success: false, 
      message: 'Nincs megfelelő kontextus a validáláshoz' 
    }));
  }

  /**
   * Edit contact value
   */
  editContact(context: ContactContext, newValue: string): Observable<ValidationResult> {
    if (!newValue?.trim()) {
      return throwError(() => ({ 
        success: false, 
        message: 'Üres érték nem menthető' 
      }));
    }

    if (newValue === context.contactValue) {
      return new Observable(subscriber => {
        subscriber.next({ success: true, message: 'Nincs változás' });
        subscriber.complete();
      });
    }

    const updateRequest: ContactEditRequest = {
      old_value: context.contactValue,
      new_value: newValue.trim(),
      contact_type: context.contactType
    };

    // Determine endpoint based on context
    if (context.context === 'customer' && context.customerId) {
      return this.uploadService.updateCustomerContactValue(context.customerId, updateRequest)
        .pipe(
          map(() => ({
            success: true,
            message: `Kontakt sikeresen frissítve: "${context.contactValue}" → "${newValue}"`
          })),
          catchError(error => throwError(() => ({ 
            success: false, 
            message: this.getEditErrorMessage(error) 
          })))
        );
    } else if (context.companyId) {
      return this.uploadService.updateContactValue(context.companyId, updateRequest)
        .pipe(
          map(() => ({
            success: true,
            message: `Kontakt sikeresen frissítve: "${context.contactValue}" → "${newValue}"`
          })),
          catchError(error => throwError(() => ({ 
            success: false, 
            message: this.getEditErrorMessage(error) 
          })))
        );
    }

    return throwError(() => ({ 
      success: false, 
      message: 'Nincs megfelelő kontextus a szerkesztéshez' 
    }));
  }

  /**
   * Copy contact to clipboard
   */
  copyToClipboard(text: string): Observable<ValidationResult> {
    return from(navigator.clipboard.writeText(text)).pipe(
      map(() => ({
        success: true,
        message: `"${text}" a vágólapra másolva`
      })),
      catchError(() => throwError(() => ({ 
        success: false, 
        message: 'Másolás sikertelen' 
      })))
    );
  }

  /**
   * Create validation interaction log
   */
  createValidationInteraction(
    contactValue: string, 
    contactType: 'email' | 'phone', 
    validStatus: boolean,
    companyId?: number,
    customerId?: number
  ): Observable<any> {
    // Find ownership of the contact
    const statusText = validStatus ? 'Megerősítve' : 'Elutasítva';
    const affectedEntitiesData = {
      type: 'contact_validated',
      entity_type: contactType,
      status_text: statusText,
      entity_value: contactValue,
      valid_status: validStatus
    };
    
    const interactionData: any = {
      status: 'Kontakt módosítás',
      affected_entities: JSON.stringify([affectedEntitiesData])
    };

    if (customerId) {
      interactionData.customerid = customerId;
    } else if (companyId) {
      interactionData.companyid = companyId;
    }

    return this.interactionsService.createInteraction(interactionData);
  }

  /**
   * Update local contact data after successful API calls
   */
  updateLocalContactData(
    company: InsuranceCompany | null,
    contactValue: string,
    contactType: 'email' | 'phone',
    updateType: 'validation' | 'edit',
    newValue?: string | boolean
  ): void {
    if (!company) return;

    if (contactType === 'email') {
      // Update company emails
      if (company.emails) {
        const emailItem = company.emails.find(e => e.email === contactValue);
        if (emailItem) {
          if (updateType === 'validation') {
            emailItem.validstatus = newValue as boolean;
          } else if (updateType === 'edit' && typeof newValue === 'string') {
            emailItem.email = newValue;
          }
        }
      }

      // Update customer emails
      if (company.relatedCustomers) {
        for (const customer of company.relatedCustomers) {
          if (customer.emails) {
            const emailItem = customer.emails.find(e => e.email === contactValue);
            if (emailItem) {
              if (updateType === 'validation') {
                emailItem.validstatus = newValue as boolean;
              } else if (updateType === 'edit' && typeof newValue === 'string') {
                emailItem.email = newValue;
              }
            }
          }
        }
      }
    } else if (contactType === 'phone') {
      // Update company phones
      if (company.phones) {
        const phoneItem = company.phones.find(p => p.phone === contactValue);
        if (phoneItem) {
          if (updateType === 'validation') {
            phoneItem.validstatus = newValue as boolean;
          } else if (updateType === 'edit' && typeof newValue === 'string') {
            phoneItem.phone = newValue;
          }
        }
      }

      // Update customer phones
      if (company.relatedCustomers) {
        for (const customer of company.relatedCustomers) {
          if (customer.phones) {
            const phoneItem = customer.phones.find(p => p.phone === contactValue);
            if (phoneItem) {
              if (updateType === 'validation') {
                phoneItem.validstatus = newValue as boolean;
              } else if (updateType === 'edit' && typeof newValue === 'string') {
                phoneItem.phone = newValue;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Update local contact data for individual customer after successful API calls
   */
  updateIndividualCustomerContactData(
    individualCustomer: RelatedCustomer | null,
    contactValue: string,
    contactType: 'email' | 'phone',
    updateType: 'validation' | 'edit',
    newValue?: string | boolean
  ): void {
    if (!individualCustomer) return;

    if (contactType === 'email') {
      // Update individual customer emails
      if (individualCustomer.emails) {
        const emailItem = individualCustomer.emails.find(e => e.email === contactValue);
        if (emailItem) {
          if (updateType === 'validation') {
            emailItem.validstatus = newValue as boolean;
          } else if (updateType === 'edit' && typeof newValue === 'string') {
            emailItem.email = newValue;
          }
        }
      }
    } else if (contactType === 'phone') {
      // Update individual customer phones
      if (individualCustomer.phones) {
        const phoneItem = individualCustomer.phones.find(p => p.phone === contactValue);
        if (phoneItem) {
          if (updateType === 'validation') {
            phoneItem.validstatus = newValue as boolean;
          } else if (updateType === 'edit' && typeof newValue === 'string') {
            phoneItem.phone = newValue;
          }
        }
      }
    }
  }

  /**
   * Determine contact ownership within company structure
   */
  findContactOwnership(
    company: InsuranceCompany | null,
    contactValue: string,
    contactType: 'email' | 'phone'
  ): { companyId?: number; customerId?: number } {
    if (!company) return {};

    // Check if contact belongs to company directly
    if (contactType === 'email') {
      const companyEmail = company.emails?.find(e => e.email === contactValue);
      if (companyEmail) {
        return { companyId: company.id };
      }
    } else if (contactType === 'phone') {
      const companyPhone = company.phones?.find(p => p.phone === contactValue);
      if (companyPhone) {
        return { companyId: company.id };
      }
    }

    // Check related customers
    if (company.relatedCustomers) {
      for (const customer of company.relatedCustomers) {
        if (contactType === 'email') {
          const customerEmail = customer.emails?.find(e => e.email === contactValue);
          if (customerEmail) {
            return { customerId: customer.id };
          }
        } else if (contactType === 'phone') {
          const customerPhone = customer.phones?.find(p => p.phone === contactValue);
          if (customerPhone) {
            return { customerId: customer.id };
          }
        }
      }
    }

    return {};
  }

  private getValidationMessage(contactValue: string, validStatus: boolean, unchanged?: boolean): string {
    if (unchanged) {
      const statusText = validStatus ? 'érvényes' : 'érvénytelen';
      return `"${contactValue}" státusza már ${statusText} volt, nem szükséges frissítés`;
    }
    
    const actionText = validStatus ? 'érvényesként jelölve' : 'érvénytelenként jelölve';
    return `"${contactValue}" ${actionText}`;
  }

  private getEditErrorMessage(error: any): string {
    if (error?.error?.detail) {
      return error.error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (error?.status === 0) {
      return 'Kapcsolat hiba - a backend nem elérhető';
    } else if (error?.status === 404) {
      return 'Kontakt nem található';
    }
    
    return 'Hiba történt a kontakt frissítése során';
  }
}